import { useLocation } from '@tanstack/react-router';

/**
 * What `navigate({ …, state })` may carry to the next page. The app declares its keys by augmenting it
 * (`declare module '@luminovo/design-system' { interface NavigationState … }`); a package declares a key it writes
 * next to the code that writes it. Only declared keys can be written or read; TanStack's own keys on the same object
 * (`key`, `__TSR_index` and the like) aren't part of it.
 *
 * Declare every key optional: the state is missing on a shared link, a bookmark or a URL typed into a new tab.
 * Nothing checks the values, and the entry survives a reload and Back, so an entry an older build wrote may lack a
 * key or hold another shape. Keep values structured-clone-safe (plain data, no functions or class instances) and
 * small: anything a user might bookmark or reload into belongs in the query string.
 */
export interface NavigationState {}

// So `navigate`'s `state` and what `useHistoryState` reads are typed by the same declaration.
declare module '@tanstack/history' {
    interface HistoryState extends NavigationState {}
}

/**
 * One value of the state the navigation to this history entry carried; re-renders only when it changes.
 *
 * Read it for what a page starts with (a form's initial values, where the user came from). It moves to the next entry
 * as soon as a navigation away starts, while the page being left is still mounted, so an effect that acts on a change
 * of it (reports, redirects) acts on the next page's state. Never use it to tell which page you're on: that is
 * `useRouteName()` or `useIsRouteActive()`.
 */
export function useHistoryState<T>(select: (state: Readonly<NavigationState>) => T): T {
    // Cast through `unknown`: TanStack's options type asks whether the selection is JSON (for structural sharing),
    // which it can't decide for a type parameter.
    const selected: unknown = useLocation({ select: (location): unknown => select(location.state) });
    return selected as T;
}
